/**
 * This file contains bundled prompt templates that are included directly in the package,
 * with support for the new Handlebars template system.
 *
 * IMPORTANT: This system now supports loading prompts from both bundled sources and
 * the template system. The template system is preferred when available, with bundled
 * prompts serving as a fallback.
 *
 * All prompts must be defined here and accessed through the getBundledPrompt function.
 * This ensures that the system always has access to the prompts it needs, regardless of
 * where it's installed or how it's packaged.
 */

import type { ReviewType } from '../types/review';
import logger from '../utils/logger';
import { checkTemplatesAvailability, getPromptTemplate } from '../utils/promptTemplateManager';

// Flag to control whether to use the template system (can be configured at runtime)
export const USE_TEMPLATE_SYSTEM = true;

// Map of prompt templates by review type and language/framework
export const bundledPrompts: Record<string, Record<string, string>> = {
  // Generic prompts (no language)
  generic: {
    architectural: `# Architectural Code Review

Analyze code architecture focusing on:

- **Overall Architecture**: Patterns and their appropriateness
- **Component Structure**: Organization into components, modules, or services
- **Dependency Management**: How dependencies are managed and injected
- **Separation of Concerns**: Whether responsibilities are properly separated
- **Code Reusability**: Opportunities for better code reuse
- **Scalability**: How well the architecture would scale
- **Maintainability**: Ease of maintenance and extension
- **Package Integration**: Opportunities to leverage established OSS packages

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of the codebase architecture
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., ARCH-001, ARCH-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Design Patterns, Component Structure, Dependency Management, Separation of Concerns, Code Reusability, Scalability, Maintainability, Package Integration
- **description**: Detailed explanation of the issue
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and potential consequences
- **recommendation**: Specific, actionable fix with code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of architectural improvements, ordered by impact and effort. Where appropriate, include mature OSS packages that could replace custom implementations.

{{LANGUAGE_INSTRUCTIONS}}

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    'quick-fixes': `# Quick Fixes Code Review

Identify quick, high-impact improvements that don't require major architectural changes:

- Code style and formatting issues
- Simple logic errors or bugs
- Obvious performance optimizations
- Readability improvements
- Small-scale refactoring opportunities
- Unhandled edge cases

{{CI_DATA}}

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of quick-fix opportunities in the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., QF-001, QF-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Code Style, Logic Error, Performance, Readability, Refactoring, Edge Cases, Error Handling, TypeScript Errors, Lint Violations
- **description**: Detailed explanation of the issue
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and what impact fixing it would have
- **recommendation**: Specific, actionable fix with before/after code examples
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of quick fixes, ordered by impact and effort. Group by severity with the most critical fixes listed first.

IMPORTANT: Include fixes for any TypeScript compilation errors or ESLint violations from the CI data above.

{{LANGUAGE_INSTRUCTIONS}}

{{SCHEMA_INSTRUCTIONS}}`,

    consolidated: `# Consolidated Code Review

Analyze comprehensively and report by priority across:

- **Code Quality & Bugs**: Bugs, logic errors, null/undefined issues, missing error handling
- **Architecture & Structure**: Code organization, separation of concerns, modularity
- **Security Concerns**: Vulnerabilities, exposed credentials, input validation
- **Performance**: Inefficient algorithms, optimization opportunities
- **Documentation & Testing**: Code documentation, comments, test coverage
- **Best Practices**: Language-specific best practices and patterns

## Grading Requirements

Grade the codebase using the academic scale (A+ to F) for:
- **Overall Grade**: Letter grade with justification
- **Category Grades** (each with score and grade):
  - **Functionality**: Achieves intended purpose
  - **Code Quality**: Best practices, clean code
  - **Documentation**: Comments, README, inline docs
  - **Testing**: Coverage, quality, edge cases
  - **Maintainability**: Organization, modularity
  - **Security**: Validation, authentication, security practices
  - **Performance**: Algorithm efficiency, resource usage

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of the codebase quality
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)
- **Overall Grade**: Letter grade (A+ to F) with justification (alongside the 0.0-1.0 score)
- **Category Grades**: Individual letter grades (A+ to F) for each category with explanations:
  - **Functionality** score and grade
  - **Code Quality** score and grade
  - **Documentation** score and grade
  - **Testing** score and grade
  - **Maintainability** score and grade
  - **Security** score and grade
  - **Performance** score and grade

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., CRIT-001, CRIT-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Code Quality, Architecture, Security, Performance, Documentation, Testing, Best Practices
- **description**: Detailed explanation of the issue
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and potential consequences
- **recommendation**: Specific, actionable fix with code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements, ordered by impact and effort. Include strengths and positive aspects of the codebase.

{{LANGUAGE_INSTRUCTIONS}}

{{CI_DATA}}`,

    security: `# Security Code Review

Analyze code for security vulnerabilities focusing on:

- **Input Validation**: All inputs properly validated
- **Authentication & Authorization**: Identity verification and access control
- **Data Protection**: Sensitive data handling, storage, and transmission
- **Dependency Security**: Vulnerable dependencies
- **Common Vulnerabilities**: XSS, CSRF, SQL injection, command injection, etc.
- **Error Handling**: Errors handled securely without leaking sensitive info
- **Secure Coding Practices**: Adherence to secure coding standards

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall security assessment of the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., SEC-001, SEC-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Input Validation, Authentication, Authorization, Data Protection, Dependency Security, XSS, CSRF, SQL Injection, Command Injection, Secure Configuration, Error Handling, OWASP Top 10
- **description**: Detailed explanation of the vulnerability
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters, potential attack vectors, and consequences
- **recommendation**: Specific, actionable remediation with code examples where appropriate. Reference relevant security standards (OWASP, CWE, NIST) where applicable.
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of security improvements, ordered by severity and exploitability.

{{LANGUAGE_INSTRUCTIONS}}

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    performance: `# Performance Code Review

Analyze code for performance issues and optimization opportunities focusing on:

- **Algorithmic Efficiency**: Inefficient algorithms or data structures
- **Resource Usage**: CPU, memory, network, and disk utilization
- **Concurrency**: Parallelism and asynchronous operation handling
- **Caching**: Caching opportunities or improvements to existing caching
- **Database Interactions**: Query efficiency and data access patterns
- **UI Performance**: Rendering performance for frontend code
- **Load and Scale**: Performance under high load

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall performance assessment of the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., PERF-001, PERF-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Algorithmic Efficiency, Resource Usage, Concurrency, Caching, Database, UI Rendering, Network, Memory Management, Load and Scale, Bundle Size
- **description**: Detailed explanation of the performance issue
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters, estimated performance impact, and potential consequences at scale
- **recommendation**: Specific, actionable optimization with before/after code examples. Include measurement suggestions where applicable.
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of performance optimizations, ordered by impact and effort.

{{LANGUAGE_INSTRUCTIONS}}

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    'unused-code': `# Unused Code Review

Identify unused or dead code including:
- Unused variables, functions, classes, or modules
- Dead code paths that can never execute
- Redundant or duplicate code
- Commented-out code to remove
- Deprecated features no longer needed

For each candidate: check references (direct and indirect), assess future-use intent, evaluate removal risk, and provide confidence level.

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of unused/dead code in the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)
- Total count of unused elements found, grouped by type

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., UNUSED-001, UNUSED-002)
- **title**: Brief descriptive title identifying the unused element
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Unused Variable, Unused Function, Unused Class, Unused Import, Unused Module, Dead Code Path, Redundant Code, Commented-Out Code, Deprecated Feature
- **description**: Detailed explanation of why this element is considered unused, including evidence and reasoning
- **location**: File path and line number(s) where the element is defined
- **impact**: Why this matters — maintenance burden, confusion risk, bundle size impact
- **recommendation**: Specific guidance — whether to remove, refactor, or document the element, with code examples
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are that this is truly unused)

### Recommendations
Prioritized list of cleanup actions, ordered by confidence and impact.

{{LANGUAGE_INSTRUCTIONS}}

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    'best-practices': `# Best Practices Code Review

{{LANGUAGE_INSTRUCTIONS}}

Evaluate adherence to best practices across:

### 1. Code Organization & Structure
- File/directory organization, separation of concerns, naming conventions, logical grouping

### 2. Design Patterns & Architecture
- Common design patterns, clean interfaces, dependency injection, component reusability

### 3. Error Handling & Robustness
- Error handling, graceful failures, input validation, edge cases

### 4. Performance Optimization
- Algorithm and data structure selection, resource efficiency, caching, anti-pattern avoidance

### 5. Maintainability & Readability
- Self-documenting code, consistent formatting, avoidance of code smells, testability

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall best-practices assessment of the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., BP-001, BP-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Code Organization, Design Patterns, Error Handling, Performance Optimization, Maintainability, Readability, Testing, Documentation, Security Hygiene
- **description**: Detailed explanation of the pattern or practice that could be improved
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters — maintainability, performance, readability, or team velocity impact
- **recommendation**: Specific, actionable guidance with code examples and reference to the established pattern or principle
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements, ordered by impact and effort. Balance theoretical best practices with pragmatic considerations for the specific codebase context.

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}

Remember to balance theoretical best practices with pragmatic considerations for the specific codebase context.`,

    evaluation: `# Candidate Technical Assessment - Developer Evaluation

🚨 CRITICAL: This is a CANDIDATE COMPETENCY EVALUATION, NOT a code review. You are assessing the DEVELOPER'S skills, experience, and readiness based on their code. DO NOT provide code improvement suggestions.

Act as a **senior technical hiring manager with 15+ years of experience evaluating developers for critical production systems**. Your role is to assess whether this candidate should be hired and at what level, based on the code they've written.

## Assessment Objectives:
- **Developer Skill Level**: Determine actual technical competency (1-10 scale)
- **Experience Level**: Estimate years of professional experience
- **Production Readiness**: Can they work on critical systems safely?
- **Hiring Decision**: Should we hire them? At what level?
- **AI Detection**: Did they use AI tools to write this code?

## Assessment Focus Areas:
🔍 **Technical Competency**: Architecture decisions, security awareness, error handling
🚨 **Red Flags**: Dangerous patterns, security vulnerabilities, production risks
⚡ **Professional Maturity**: Code organization, best practices, maintainability
🤖 **AI Usage Detection**: Patterns suggesting AI-generated code
🎯 **Hiring Recommendation**: Specific role level and conditions

## Systematic Analysis Framework:
☐ **Project Structure & Architecture**: Overall organization, separation of concerns, scalability
☐ **Language-Specific Usage**: Type safety, framework patterns, best practices adherence
☐ **Component/Module Design**: Reusability, maintainability, interface design
☐ **State Management & Data Flow**: Architecture decisions, data handling patterns
☐ **Build Configuration & Tooling**: Development setup, automation, professional practices
☐ **Testing Approach**: Coverage, quality, testing patterns and maturity
☐ **Git History & Development Workflow**: Commit quality, development process, collaboration
☐ **Error Handling & Edge Cases**: Defensive programming, robustness, production readiness
☐ **Security & Performance**: Security awareness, performance considerations, optimization
☐ **Final Assessment & Recommendation**: Overall competency evaluation and hiring decision

DELIVER: Brutally honest assessment focused on hiring decision. This determines if they get the job.

CRITICAL OUTPUT REQUIREMENT: You MUST use the EXACT section headers and format provided below. Fill in the bracketed placeholders with your assessment. Do not add extra sections or change the structure.

YOUR RESPONSE MUST START WITH THE FOLLOWING EXACT TEXT:

## Developer Competency Evaluation

### Overall Assessment
**Technical Competency:** [SCORE] - [JUSTIFICATION]
**Years of Experience:** [RANGE]
**Developer Level:** [LEVEL]
**Production Readiness:** [STATUS]

> **Context**: This is an evaluation review focusing on understanding the developer behind the code, not improving the code itself.

## Critical Assessment Framework

### 1. Technical Competency Analysis

**Critical Skill Gaps (Red Flags):**
- Dangerous code patterns that expose security vulnerabilities
- Failure to validate inputs or sanitize outputs
- Missing critical error handling for production scenarios
- Performance anti-patterns that would fail at scale
- Hardcoded values where configuration is essential
- Synchronous operations where async is required
- Memory leaks or resource management failures
- Lack of defensive programming practices

**Production Readiness Indicators:**
- Understanding of security implications (SQL injection, XSS, CSRF)
- Proper secrets management and configuration
- Scalability considerations in architecture
- Error recovery and graceful degradation
- Logging and observability implementation
- Resource cleanup and connection management
- Concurrency and race condition awareness

**Experience Level Markers:**
- **0-2 years (Novice/Junior)**: Tutorial-following patterns, basic CRUD operations, minimal error handling
- **2-5 years (Junior/Mid-Level)**: Framework proficiency, some patterns, basic security awareness
- **5-8 years (Senior)**: Architecture decisions, performance optimization, security-first thinking
- **8-12 years (Staff)**: Platform thinking, operational excellence, mentorship patterns
- **12+ years (Principal)**: System design mastery, cross-team impact, strategic technical decisions

### 2. AI-Generated Code Detection

**Clear AI Generation Indicators (>80% likelihood):**
- Accepting dangerous boilerplate without security modifications
- Generic error messages lacking context ("An error occurred")
- Over-engineered abstractions for simple problems
- Missing domain-specific optimizations any experienced dev would include
- Inconsistent expertise levels within same codebase
- Tutorial-perfect syntax with fundamental logic flaws
- Comments explaining obvious code while missing critical context
- Framework misuse that suggests pattern matching without understanding
- Unnecessary complexity - using design patterns where simple functions suffice
- Perfect formatting but poor logical flow
- Generic variable names (data, result, item) throughout
- Copy-paste patterns with slight variations that don't make sense

**Specific AI Tells to Look For:**
- **Documentation Mismatch**: Overly detailed comments for trivial code, no comments for complex logic
- **Import Bloat**: Importing entire libraries for single functions
- **Error Handling Theater**: Try-catch blocks that catch and immediately re-throw
- **Configuration Confusion**: Mixing environment configs, hardcoded values, and ENV vars randomly
- **Testing Patterns**: Tests that test the framework, not the business logic
- **Async Abuse**: Using async/await where synchronous would be simpler and correct
- **Type Over-Engineering**: Complex generic types for simple use cases
- **Dead Code**: Unused functions/variables that seem like "just in case" additions

**Human Development Patterns:**
- Incremental complexity matching problem evolution
- Personal coding quirks and consistent style
- Context-aware shortcuts and pragmatic choices
- Evidence of debugging and problem-solving (console.logs, commented attempts)
- Natural evolution of architecture (can see the journey)
- Domain-specific knowledge application
- Opinionated technology choices with clear rationale
- Appropriate laziness - not reinventing wheels unnecessarily
- Evidence of real-world constraints (deadlines, technical debt comments)

### 3. Critical Decision Analysis

**Critical Decision Points:**
- **Security Decisions**: Are they making choices that expose systems to attacks?
- **Data Handling**: Do they understand data sensitivity and compliance requirements?
- **Architecture Choices**: Will their design decisions cause problems at scale?
- **Dependency Management**: Are they introducing supply chain risks?
- **Error Handling**: Will their app fail catastrophically or gracefully?
- **Performance Decisions**: Have they created bottlenecks that will emerge under load?
- **Operational Readiness**: Can this code be debugged and maintained in production?

**Judgment Quality Indicators:**
- Awareness of trade-offs and explicit decision documentation
- Understanding of failure modes and mitigation strategies
- Recognition of security implications in design choices
- Appropriate complexity for the problem domain
- Evidence of thinking beyond happy path scenarios

### 4. Production System Readiness

**Ready for Production Systems:**
- Defensive programming against malicious inputs
- Proper error boundaries and circuit breakers
- Resource limits and timeout implementations
- Security headers and CORS configuration
- Audit logging and compliance considerations
- Database transaction management
- Proper async/await and promise handling
- Memory management and garbage collection awareness

**NOT Ready for Production (Requires Supervision):**
- Happy-path-only implementations
- Unhandled promise rejections
- SQL queries vulnerable to injection
- Exposed sensitive data in logs or responses
- Missing authentication/authorization checks
- Synchronous blocking operations
- Unbounded loops or recursive calls
- Resource leaks (connections, file handles, memory)

### 5. Risk Assessment for Team Integration

**High-Risk Indicators:**
- Cowboy coding without considering team impact
- Ignoring established patterns and conventions
- Making breaking changes without migration paths
- Poor git hygiene (force pushes, massive commits)
- Lack of communication in code reviews
- Introducing dependencies without team consensus
- Disregarding security or compliance requirements

**Low-Risk/High-Value Indicators:**
- Following team conventions even when disagreeing
- Clear communication of technical decisions
- Incremental, reviewable changes
- Proactive identification of risks
- Knowledge sharing and documentation
- Respectful disagreement and compromise
- Focus on team velocity over individual preferences

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall developer competency assessment (2-3 sentences, frank and direct)
- Overall confidence score (0.0-1.0)
- **Technical Competency**: Score 1-10 with one-line justification
- **Years of Experience**: Estimated range
- **Developer Level**: Novice | Junior | Mid-Level | Senior | Staff | Principal
- **Production Readiness**: Ready | Not Ready | Requires Mentorship

### Findings Array
Each finding represents a specific competency observation and must include:
- **id**: Unique identifier (e.g., EVAL-001, EVAL-002)
- **title**: Brief descriptive title of the observed pattern or issue
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO (maps to risk level for production use)
- **category**: One of: Security Posture, Error Handling, Architecture Maturity, Production Readiness, Code Quality, AI Generation Indicator, Competency Gap, Demonstrated Strength, Team Fit Risk
- **description**: Detailed explanation of the observed pattern with specific evidence from the code
- **location**: File path and line number(s) where the pattern was observed
- **impact**: Why this matters — hiring risk, production risk, or team impact
- **recommendation**: Specific guidance (supervision requirements, systems to restrict, skills to develop)
- **effort**: LOW | MEDIUM | HIGH (training/improvement effort required)
- **confidence**: 0.0-1.0 (how confident you are in this observation)

### Recommendations

IMPORTANT: You MUST also include this EXACT structured assessment section:

## Developer Competency Evaluation

### Overall Assessment
**Technical Competency:** [INSERT SCORE 1-10 HERE] - [INSERT ONE-LINE JUSTIFICATION HERE]
**Years of Experience:** [INSERT RANGE e.g., 2-4 years]
**Developer Level:** [SELECT ONE: Novice/Junior/Mid-Level/Senior/Staff/Principal]
**Production Readiness:** [SELECT ONE: Ready/Not Ready/Requires Mentorship]

### Critical Findings

#### Red Flags & Risks
[REQUIRED: List at least 3-5 specific CRITICAL/HIGH severity findings. Be direct and critical.]
- [INSERT SPECIFIC DANGEROUS PATTERN FOUND]
- [INSERT SECURITY VULNERABILITY OR RISK]
- [INSERT PRODUCTION-BREAKING ISSUE]

#### Competency Gaps
[REQUIRED: List 3-4 specific HIGH/MEDIUM severity skill gaps.]
- [INSERT MISSING ESSENTIAL SKILL]
- [INSERT AREA NEEDING IMMEDIATE IMPROVEMENT]

#### Demonstrated Strengths
[OPTIONAL: List 1-2 genuine INFO/LOW severity strengths if any. Keep brief.]
- [INSERT GENUINE STRENGTH IF ANY]

### AI Code Generation Assessment
**Likelihood:** [INSERT PERCENTAGE 0-100]%
**Confidence:** [SELECT: High/Medium/Low]
**Evidence:**
- [INSERT SPECIFIC AI PATTERN DETECTED]
- [INSERT COPY-PASTE INDICATOR]
- [INSERT MISSING OPTIMIZATION A HUMAN WOULD ADD]

### Hiring Recommendation

**Verdict:** [SELECT ONE: Strong Hire/Hire/Conditional Hire/No Hire]
**Appropriate Level:** [INSERT SPECIFIC LEVEL: e.g., Novice, Junior I, Junior II, Mid-Level I, Mid-Level II, Senior I, Senior II, Staff, Principal]

**Conditions/Concerns:**
- [INSERT SPECIFIC CONDITION OR REQUIREMENT]
- [INSERT SUPERVISION/MENTORSHIP NEEDS]
- [INSERT SYSTEMS THEY MUST NOT ACCESS]

### Critical Context

**Security Posture:** [SELECT ONE: Strong/Adequate/Weak/Dangerous]
**Architecture Maturity:** [SELECT GRADE: A/B/C/D/F]
**Team Fit Risk:** [SELECT ONE: Low/Medium/High]

### Code Quality Grades

**Architectural Sophistication:** [GRADE: A/B/C/D/F] - [Brief justification]
**Security Practices:** [GRADE: A/B/C/D/F] - [Brief justification]
**Test Coverage & Quality:** [GRADE: A/B/C/D/F] - [Brief justification]
**Documentation:** [GRADE: A/B/C/D/F] - [Brief justification]
**Best Practices Adherence:** [GRADE: A/B/C/D/F] - [Brief justification]
**Code Maintainability:** [GRADE: A/B/C/D/F] - [Brief justification]
**Performance Awareness:** [GRADE: A/B/C/D/F] - [Brief justification]
**Error Handling:** [GRADE: A/B/C/D/F] - [Brief justification]

NOTE: This assessment is based solely on code analysis patterns. No code improvements or suggestions have been provided as this is a pure developer evaluation.

{{SCHEMA_INSTRUCTIONS}}`,

    comprehensive: `# Comprehensive Code Review

Analyze code across all perspectives in a single review:

### 1. Quick Fixes & Best Practices
- Code quality: syntax, formatting, naming, style
- Language-specific patterns and idioms
- Error handling: exceptions, validation, defensive programming
- Code organization: file structure, imports, modularity
- Documentation and code clarity
- Testing: structure, coverage, quality

### 2. Security Analysis
- Input validation and sanitization
- Authentication and authorization: access control, permissions
- Data protection: sensitive data, encryption, storage
- Injection vulnerabilities: SQL, XSS, command injection
- Dependency security: third-party vulnerabilities
- Configuration security: secrets management

### 3. Performance Analysis
- Algorithmic efficiency: time and space complexity
- Resource management: memory, file handles, cleanup
- Database: query optimization, connection management
- Caching strategies
- Async operations and concurrency
- Network: API calls, data transfer, latency

### 4. Architectural Assessment
- Design pattern implementation
- Separation of concerns
- Dependency management: coupling, cohesion, injection
- Extensibility and maintainability

{{LANGUAGE_INSTRUCTIONS}}

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    'coding-test': `# Coding Test Assessment Review

You are a **Senior Technical Evaluator** conducting a comprehensive coding test assessment. Your role is to evaluate the candidate's submission against the assignment requirements and provide structured feedback using the specified evaluation criteria.

## Your Task

Analyze the provided code submission and evaluate it against the following criteria:

1. **Correctness**: Does the code fulfill all functional requirements?
2. **Code Quality**: Is the code well-structured, readable, and maintainable?
3. **Architecture**: Are appropriate design patterns and architectural decisions used?
4. **Performance**: Are there any performance considerations or optimizations?
5. **Testing**: Is the code properly tested with appropriate coverage?

## Evaluation Framework

### Functional Correctness Assessment
- **Requirement Compliance**: Verify all stated requirements are implemented
- **Core Functionality**: Assess primary feature implementation
- **Edge Cases**: Evaluate handling of boundary conditions
- **Error Scenarios**: Review error handling and validation
- **Input/Output**: Validate data processing and response formats

### Code Quality Analysis
- **Readability**: Clear variable names, consistent formatting, logical structure
- **Maintainability**: Modular design, separation of concerns, documentation
- **Code Style**: Adherence to language conventions and best practices
- **Complexity**: Appropriate complexity levels, avoidance of over-engineering
- **Documentation**: Comments, README, inline documentation quality

### Architectural Evaluation
- **Design Patterns**: Appropriate use of established patterns
- **Structure**: Logical organization of files and modules
- **Scalability**: Design considerations for growth
- **Extensibility**: Ease of adding new features
- **Dependencies**: Appropriate use of external libraries

### Performance Considerations
- **Efficiency**: Algorithm complexity and optimization
- **Resource Usage**: Memory and CPU utilization
- **Scalability**: Performance under load
- **Bottlenecks**: Identification of potential performance issues
- **Optimization**: Evidence of performance-conscious decisions

### Testing Quality
- **Coverage**: Extent of test coverage across functionality
- **Test Types**: Unit, integration, and end-to-end testing
- **Test Quality**: Meaningful test cases and assertions
- **Edge Case Testing**: Coverage of boundary conditions
- **Test Organization**: Structure and maintainability of tests

## Scoring System

Provide scores for each criterion:
- **Correctness**: 30% weight
- **Code Quality**: 25% weight
- **Architecture**: 20% weight
- **Performance**: 15% weight
- **Testing**: 10% weight

Use a 1-10 scale for each criterion, where:
- 1-3: Poor/Inadequate
- 4-6: Adequate/Needs Improvement
- 7-8: Good/Meets Standards
- 9-10: Excellent/Exceeds Standards

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall score and pass/fail status
- Key strengths and weaknesses
- Recommendation for hiring level (if applicable)
- Overall confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., CT-001, CT-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Correctness, Code Quality, Architecture, Performance, Testing, Documentation, Best Practices
- **description**: Detailed explanation of the issue or observation
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters for the assessment
- **recommendation**: Specific, actionable improvement with code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Detailed Evaluation (keep scoring structure)

#### Correctness (30% weight)
- **Score**: X/10
- **Assessment**: [Detailed analysis]
- **Strengths**: [Specific examples]
- **Areas for Improvement**: [Specific examples]

#### Code Quality (25% weight)
- **Score**: X/10
- **Assessment**: [Detailed analysis]
- **Strengths**: [Specific examples]
- **Areas for Improvement**: [Specific examples]

#### Architecture (20% weight)
- **Score**: X/10
- **Assessment**: [Detailed analysis]
- **Strengths**: [Specific examples]
- **Areas for Improvement**: [Specific examples]

#### Performance (15% weight)
- **Score**: X/10
- **Assessment**: [Detailed analysis]
- **Strengths**: [Specific examples]
- **Areas for Improvement**: [Specific examples]

#### Testing (10% weight)
- **Score**: X/10
- **Assessment**: [Detailed analysis]
- **Strengths**: [Specific examples]
- **Areas for Improvement**: [Specific examples]

### Recommendations
Prioritized list of improvements ordered by severity and impact:
- CRITICAL/HIGH severity findings to address first
- MEDIUM severity suggestions
- LOW/INFO best practices to adopt
- Resources for learning

### Technical Metrics
- Language and framework detected
- Lines of code analyzed
- Files reviewed
- Test coverage (if available)
- Performance characteristics

{{LANGUAGE_INSTRUCTIONS}}

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,
  },

  // TypeScript-specific prompts
  typescript: {
    architectural: `# TypeScript Architectural Code Review

You are an expert TypeScript architect performing a comprehensive architectural review of a TypeScript codebase.

## Your Task

Analyze the provided TypeScript code from an architectural perspective, focusing on:

1. **Overall Architecture**: Identify the architectural patterns and evaluate their appropriateness for a TypeScript project
2. **Type System Usage**: Assess how effectively TypeScript's type system is being utilized
3. **Interface Design**: Evaluate the design of interfaces and type definitions
4. **Component Structure**: Assess how the code is organized into components, modules, or services
5. **Dependency Management**: Evaluate how dependencies are managed and injected
6. **Separation of Concerns**: Check if responsibilities are properly separated
7. **Code Reusability**: Identify opportunities for better code reuse through TypeScript features
8. **Scalability Considerations**: Assess how well the architecture would scale
9. **Maintainability**: Evaluate how easy the codebase would be to maintain and extend

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of the TypeScript codebase architecture
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., ARCH-001, ARCH-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Type System Design, Interface Architecture, Component Structure, Dependency Management, Separation of Concerns, Code Reusability, Scalability, Maintainability, Module Organization
- **description**: Detailed explanation of the issue
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and potential consequences
- **recommendation**: Specific, actionable fix with TypeScript code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of architectural improvements, ordered by impact and effort.

This code is written in TYPESCRIPT. Please provide language-specific advice.

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    'best-practices': `# TypeScript Best Practices Code Review

You are an **expert TypeScript engineer** specializing in code quality and TypeScript-specific design patterns. Perform a detailed review focused on adherence to established TypeScript best practices.

## TypeScript Best Practices

Evaluate the code against the following TypeScript-specific best practices:

### 1. Type System Usage
- Proper use of TypeScript's type system (interfaces, types, generics, etc.)
- Avoid using 'any' type - prefer 'unknown' for unknown types
- Use union types and intersection types where appropriate
- Proper use of type narrowing and type guards
- Use readonly properties and const assertions where appropriate

### 2. TypeScript Configuration
- Recommend strict mode and other compiler options for better type safety
- Appropriate use of tsconfig.json settings
- Module resolution strategy best practices

### 3. Modern TypeScript Features
- Leverage TypeScript 4.x+ features when applicable
- Use optional chaining (?.) and nullish coalescing (??) operators
- Template literal types for string manipulation
- Proper usage of utility types (Partial, Pick, Omit, etc.)

### 4. Dependency Management
- Recommend TypeScript versions (currently 5.x is latest stable)
- Node.js version compatibility recommendations
- Recommend essential TypeScript-related tools:
  - ESLint with @typescript-eslint (v8.x+)
  - ts-node for development (v10.x+)
  - Types for libraries (@types/*)

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall TypeScript best-practices assessment of the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., BP-001, BP-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Type System Usage, TypeScript Configuration, Modern Features, Dependency Management, Code Organization, Error Handling, Maintainability, Readability
- **description**: Detailed explanation of the TypeScript-specific pattern or practice that could be improved
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters for type safety, maintainability, or performance
- **recommendation**: Specific, actionable guidance with TypeScript code examples. When applicable, include specific versions of TypeScript packages or tools.
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements, ordered by impact and effort, focusing on type safety and maintainability.

This code is written in TYPESCRIPT. Please provide language-specific advice.

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    comprehensive: `# TypeScript Comprehensive Code Review

You are an **expert TypeScript engineer and architect** performing a comprehensive code review that combines multiple analysis perspectives in a single thorough assessment.

## Your Task

Analyze the provided TypeScript code from **all** of the following perspectives in a single comprehensive review:

1. **Quick Fixes & Best Practices** - TypeScript immediate improvements and coding standards
2. **Security Analysis** - TypeScript-specific security patterns and vulnerabilities
3. **Performance Review** - TypeScript/JavaScript performance optimization
4. **Architectural Assessment** - TypeScript application architecture and design patterns

## TypeScript Comprehensive Analysis Framework

### 1. TypeScript Quick Fixes & Best Practices Analysis
- **Type System Usage**: Proper interfaces, types, generics, avoiding 'any'
- **Language Features**: Modern TypeScript syntax, utility types, type guards
- **Code Organization**: Module structure, imports, exports, declaration files
- **Configuration**: tsconfig.json optimization and compiler options
- **Error Handling**: Type-safe error handling and validation patterns
- **Testing**: Type-safe testing patterns and mock typing

### 2. TypeScript Security Analysis
- **Type Safety**: Preventing runtime errors through proper typing
- **Input Validation**: Type-safe input validation and sanitization
- **API Security**: Type-safe API design and data validation
- **Dependency Security**: @types packages and declaration file security
- **Configuration Security**: Secure TypeScript configuration practices
- **Build Security**: Secure compilation and build processes

### 3. TypeScript Performance Analysis
- **Compilation Performance**: Efficient TypeScript compilation setup
- **Runtime Performance**: Type erasure awareness and optimization
- **Bundle Analysis**: TypeScript impact on bundle size and loading
- **Type Checking**: Efficient use of TypeScript's type checking
- **Memory Management**: Proper typing for memory-efficient code
- **Build Optimization**: TypeScript build process optimization

### 4. TypeScript Architectural Assessment
- **Type Design**: Interface architecture, type composition, type hierarchy
- **Module Architecture**: TypeScript module system and dependency management
- **Framework Integration**: TypeScript integration with chosen frameworks
- **Scalability**: Type system design for large-scale applications
- **Maintainability**: Type-driven development and code organization
- **Testing Architecture**: Type-safe testing strategies and patterns

This comprehensive TypeScript review should provide a complete picture of the TypeScript codebase's health and a clear roadmap for improvement across all critical TypeScript development dimensions.

This code is written in TYPESCRIPT. Please provide TypeScript-specific advice.

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    'coding-test': `# TypeScript Coding Test Assessment Review

You are a **Senior TypeScript Technical Evaluator** conducting a comprehensive coding test assessment. Your role is to evaluate the candidate's TypeScript submission against the assignment requirements and provide structured feedback using the specified evaluation criteria.

## Your Task

Analyze the provided TypeScript code submission and evaluate it against the following criteria:

1. **Correctness**: Does the code fulfill all functional requirements with proper TypeScript implementation?
2. **Code Quality**: Is the code well-structured, readable, and maintainable using TypeScript best practices?
3. **Architecture**: Are appropriate design patterns and TypeScript architectural decisions used?
4. **Performance**: Are there performance considerations specific to TypeScript/JavaScript?
5. **Testing**: Is the code properly tested with TypeScript-aware testing patterns?

## TypeScript-Specific Evaluation Focus

### Type Safety and TypeScript Features
- **Type Definitions**: Proper use of interfaces, types, and generics
- **Type Safety**: Avoidance of \`any\`, proper type assertions
- **Advanced Types**: Utility types, conditional types, mapped types
- **Configuration**: \`tsconfig.json\` setup and compiler options
- **Module System**: Proper imports/exports and module resolution

### Framework Integration (if applicable)
- **React + TypeScript**: Component typing, hooks, context API
- **Node.js + TypeScript**: Express typing, middleware, database integration
- **Angular**: Service typing, dependency injection, decorators
- **Vue.js**: Component composition and TypeScript integration

## Evaluation Framework

### Functional Correctness Assessment
- **Requirement Compliance**: Verify all stated requirements are implemented with proper typing
- **Core Functionality**: Assess primary feature implementation with TypeScript best practices
- **Edge Cases**: Evaluate handling of boundary conditions with type safety
- **Error Scenarios**: Review error handling with proper TypeScript error types
- **Input/Output**: Validate data processing with proper type definitions

### TypeScript Code Quality Analysis
- **Type Safety**: Proper use of TypeScript's type system
- **Readability**: Clear variable names, consistent formatting, logical structure
- **Maintainability**: Modular design, separation of concerns, TSDoc documentation
- **Code Style**: Adherence to TypeScript conventions and best practices
- **Complexity**: Appropriate complexity levels, effective use of TypeScript features

### TypeScript Architectural Evaluation
- **Type Design**: Interface design, type composition, and type hierarchy
- **Module Structure**: Logical organization with proper TypeScript imports/exports
- **Dependency Management**: Proper use of \`@types\` packages and declaration files
- **Configuration**: \`tsconfig.json\` optimization for the project needs
- **Build Integration**: TypeScript compilation and build process setup

### Performance Considerations
- **Compilation Performance**: Efficient TypeScript compilation setup
- **Runtime Performance**: Type erasure awareness and optimization
- **Bundle Size**: Impact of TypeScript on final bundle size
- **Type Checking**: Efficient use of TypeScript's type checking
- **Memory Usage**: Proper management of type definitions

### Testing Quality with TypeScript
- **Type-Safe Testing**: Proper typing of test cases and mocks
- **Test Coverage**: Extent of test coverage across typed functionality
- **Test Organization**: Structure and maintainability of TypeScript tests
- **Mock Typing**: Proper typing of mocks and stubs
- **Integration Testing**: Type-safe integration tests

## TypeScript Skill Level Indicators

### Beginner Level (Junior)
- Basic interface and type definitions
- Simple type annotations
- Minimal use of generics
- Heavy use of \`any\` type
- Basic \`tsconfig.json\` setup

### Intermediate Level (Mid)
- Proper interface and type usage
- Some generics and utility types
- Union and intersection types
- Conditional type guards
- Appropriate configuration

### Advanced Level (Senior)
- Complex generic constraints
- Custom utility types
- Advanced type manipulation
- Discriminated unions
- Performance-optimized configuration

### Expert Level (Lead/Architect)
- Template literal types
- Conditional types and mapped types
- Complex type-level programming
- Custom declaration files
- Advanced compiler configuration

## Scoring System

Provide scores for each criterion:
- **Correctness**: 30% weight
- **Code Quality**: 25% weight
- **Architecture**: 20% weight
- **Performance**: 15% weight
- **Testing**: 10% weight

Use a 1-10 scale for each criterion, where:
- 1-3: Poor/Inadequate
- 4-6: Adequate/Needs Improvement
- 7-8: Good/Meets Standards
- 9-10: Excellent/Exceeds Standards

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall score and pass/fail status
- TypeScript skill level assessment
- Key strengths and weaknesses
- Recommendation for hiring level
- Overall confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., CT-001, CT-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Correctness, TypeScript Code Quality, Architecture, Performance, Testing, Type Safety, TypeScript Configuration, Documentation
- **description**: Detailed explanation of the issue or observation with TypeScript-specific context
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters for the assessment
- **recommendation**: Specific, actionable TypeScript improvement with code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Detailed Evaluation (keep scoring structure)

#### Correctness (30% weight)
- **Score**: X/10
- **Assessment**: [Detailed analysis with TypeScript context]
- **Strengths**: [Specific TypeScript examples]
- **Areas for Improvement**: [Specific TypeScript examples]

#### TypeScript Code Quality (25% weight)
- **Score**: X/10
- **Type Safety**: [Analysis of type usage and safety]
- **TypeScript Best Practices**: [Adherence to TypeScript conventions]
- **Strengths**: [Specific TypeScript examples]
- **Areas for Improvement**: [Specific TypeScript examples]

#### Architecture (20% weight)
- **Score**: X/10
- **Type Design**: [Interface and type architecture analysis]
- **Module Organization**: [TypeScript module structure]
- **Strengths**: [Specific TypeScript examples]
- **Areas for Improvement**: [Specific TypeScript examples]

#### Performance (15% weight)
- **Score**: X/10
- **TypeScript Performance**: [Compilation and runtime considerations]
- **Build Optimization**: [TypeScript build configuration]
- **Strengths**: [Specific TypeScript examples]
- **Areas for Improvement**: [Specific TypeScript examples]

#### Testing (10% weight)
- **Score**: X/10
- **Type-Safe Testing**: [TypeScript testing patterns]
- **Test Coverage**: [TypeScript test quality]
- **Strengths**: [Specific TypeScript examples]
- **Areas for Improvement**: [Specific TypeScript examples]

### TypeScript-Specific Observations
- **Type System Usage**: [Advanced TypeScript features used]
- **Configuration Quality**: [tsconfig.json assessment]
- **Framework Integration**: [How TypeScript is used with chosen framework]
- **Professional Practices**: [Evidence of TypeScript best practices]

### Recommendations
Prioritized list of improvements ordered by severity and impact:
- CRITICAL/HIGH severity findings to address first
- MEDIUM severity TypeScript suggestions
- LOW/INFO best practices to adopt
- TypeScript learning resources

### Technical Metrics
- TypeScript version and configuration
- Type safety score (estimated)
- Lines of TypeScript code analyzed
- Files reviewed
- Test coverage (if available)
- Build performance characteristics

This code is written in TYPESCRIPT. Please provide language-specific advice.

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,
  },

  // Framework-specific prompts
  'typescript:react': {
    'best-practices': `# React with TypeScript Best Practices Code Review

You are an **expert React and TypeScript engineer** specializing in modern React development patterns with TypeScript. Perform a detailed review focused on React+TypeScript best practices.

## React with TypeScript Best Practices

Evaluate the code against the following React+TypeScript-specific best practices:

### 1. Component Structure & Types
- Proper typing of props and state (use interfaces, avoid 'any')
- Use of function components with proper type definitions
- Proper typing of event handlers
- Use of React.FC vs. explicit return types
- Appropriate use of React.ComponentProps and other utility types
- Prefer explicit prop interfaces to minimize re-renders

### 2. Hooks Usage & Typing
- Proper typing of useState, useReducer, useContext
- Custom hooks with appropriate type signatures
- Use of useCallback and useMemo with proper dependency arrays
- Typing for useRef (e.g., useRef<HTMLDivElement>(null))
- Proper error handling in async hooks

### 3. State Management
- Appropriate use of context API with proper typing
- TypeScript integration with state management (Redux, Zustand, Jotai, etc.)
- Type-safe action creators and reducers
- Immutable state updates using TypeScript-friendly patterns

### 4. Dependency Recommendations
- React version:
  - Latest stable: React 19.1.0 (March 2025)
  - Previous major: React 18.x (supported until 2026)
- TypeScript version (recommend 5.x)
- Type-safe CSS solutions (styled-components, emotion, tailwind)
- Recommend testing libraries with good TypeScript support
- Specific dependencies:
  - @types/react (matching React version)
  - @types/react-dom (matching React version)
  - eslint-plugin-react-hooks (5.x+)
  - typescript-eslint (8.x+)

### 5. React 19.x Features
- Type-safe use of new preloading APIs (preload, preloadData, preconnect)
- Server Components typing practices
- Type-safe implementation of useFormStatus and useFormState
- Proper implementation of the new useActionState hook
- Safe migration from React 18.x features to React 19.x equivalents
- CSS Framework integrations:
  - Tailwind CSS v4.0 integration with React 19
  - Material UI v7.0.0 (with Pigment CSS for React Server Components)
  - Chakra UI v3.18.0 with React 19 compatibility

### 6. React 18.x Features (for projects using previous versions)
- Type-safe use of Suspense and concurrent mode
- Migration path to Server Components
- Type-safe use of createRoot and hydrateRoot
- Transition API typing best practices

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall React+TypeScript best-practices assessment of the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., BP-001, BP-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Component Structure, Hooks Usage, State Management, Type Safety, React Version Compatibility, Dependency Management, Performance, Accessibility
- **description**: Detailed explanation of the React+TypeScript-specific pattern or practice that could be improved
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters for type safety, maintainability, or performance
- **recommendation**: Specific, actionable guidance with React+TypeScript code examples. Note which React version(s) the recommendation applies to (19.x, 18.x, or both). When applicable, include specific package versions.
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements, ordered by impact and effort, focusing on type safety and maintainability.

This code is written in TYPESCRIPT for a REACT application. Please provide framework-specific advice.

{{SCHEMA_INSTRUCTIONS}}`,
  },

  // Flutter-specific prompts
  'dart:flutter': {
    'best-practices': `# Flutter with Dart Best Practices Code Review

You are an **expert Flutter and Dart engineer** specializing in modern Flutter development patterns with Dart. Perform a detailed review focused on Flutter+Dart best practices.

## Flutter with Dart Best Practices

Evaluate the code against the following Flutter+Dart-specific best practices:

### 1. Widget Structure & Architecture
- Proper widget composition and separation of concerns
- Use of StatelessWidget vs StatefulWidget appropriately
- Effective use of const constructors for performance
- Widget lifecycle management and disposal
- Proper use of keys for widget identity
- BuildContext usage and passing patterns

### 2. State Management Patterns
- Appropriate state management solution selection:
  - **Riverpod** (recommended for new projects): Type-safe, compile-time DI
  - **BLoC/Cubit**: Event-driven architecture with clear separation
  - **Provider**: Simple and lightweight for basic needs
  - **GetX**: Reactive programming with dependency injection
- Proper state lifting and data flow patterns
- Immutable state management practices
- Error handling in state management

### 3. Dart Language Best Practices
- Sound null safety implementation and migration
- Proper use of final, const, and var keywords
- Effective use of Dart 3.x features (records, patterns, sealed classes)
- Extension methods and mixins usage
- Async/await patterns and Future/Stream handling
- Proper exception handling and error propagation

### 4. Performance Optimization
- Widget rebuilding optimization (const constructors, RepaintBoundary)
- Efficient list rendering (ListView.builder, CustomScrollView)
- Image optimization and caching strategies
- Memory management and disposal patterns
- Build method optimization and avoiding expensive operations
- Proper use of keys and widget identity

### 5. UI/UX Best Practices
- Material Design 3 implementation
- Cupertino design patterns for iOS
- Responsive design and adaptive layouts
- Accessibility implementation (Semantics, screen readers)
- Theme management and dark mode support
- Animation best practices and performance

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall Flutter+Dart best-practices assessment of the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., BP-001, BP-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Widget Structure, State Management, Dart Language, Performance Optimization, UI/UX Best Practices, Accessibility, Null Safety, Async Patterns, Code Organization
- **description**: Detailed explanation of the Flutter+Dart-specific pattern or practice that could be improved
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters for performance, maintainability, or user experience
- **recommendation**: Specific, actionable guidance with Flutter/Dart code examples
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements, ordered by impact and effort.

This code is written in DART for a FLUTTER application. Please provide Flutter-specific advice.

{{SCHEMA_INSTRUCTIONS}}`,

    architectural: `# Flutter Architectural Code Review

You are an expert Flutter architect performing a comprehensive architectural review of a Flutter codebase.

## Your Task

Analyze the provided Flutter code from an architectural perspective, focusing on:

1. **Overall Architecture**: Identify architectural patterns and evaluate their appropriateness for Flutter
2. **State Management Architecture**: Assess the chosen state management solution and its implementation
3. **Widget Architecture**: Evaluate widget composition, reusability, and hierarchy
4. **Data Layer Architecture**: Assess repositories, services, and data flow patterns
5. **Navigation Architecture**: Evaluate routing and navigation patterns
6. **Dependency Management**: Evaluate how dependencies are managed and injected
7. **Scalability Considerations**: Assess how well the architecture would scale
8. **Platform Integration**: Evaluate native platform integration patterns

## Flutter-Specific Architectural Considerations

### State Management Architecture
- **Riverpod**: Provider-based architecture with compile-time safety
- **BLoC Pattern**: Event-driven architecture with clear separation of concerns
- **Provider**: Simple dependency injection and state management
- **GetX**: Reactive programming with built-in dependency injection

### Widget Architecture Patterns
- **Composition over Inheritance**: Proper widget composition strategies
- **Single Responsibility**: Each widget should have a single, well-defined purpose
- **Reusable Components**: Creation of reusable widget libraries
- **Theme Integration**: Consistent theming and styling architecture

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of the Flutter codebase architecture
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., ARCH-001, ARCH-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: State Management Architecture, Widget Architecture, Data Layer, Navigation Architecture, Dependency Management, Scalability, Platform Integration, Reusable Components, Theme Integration
- **description**: Detailed explanation of the architectural issue
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and potential consequences for the app
- **recommendation**: Specific, actionable fix with Flutter/Dart code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of architectural improvements, ordered by impact and effort.

This code is written in DART for a FLUTTER application. Please provide Flutter-specific architectural advice.

{{SCHEMA_INSTRUCTIONS}}`,

    comprehensive: `# Flutter Comprehensive Code Review

You are an **expert Flutter and Dart engineer** performing a comprehensive code review that combines multiple analysis perspectives in a single thorough assessment.

## Your Task

Analyze the provided Flutter/Dart code from **all** of the following perspectives in a single comprehensive review:

1. **Quick Fixes & Best Practices** - Flutter/Dart immediate improvements
2. **Security Analysis** - Mobile app security and Flutter-specific vulnerabilities
3. **Performance Review** - Flutter performance optimization and mobile efficiency
4. **Architectural Assessment** - Flutter app architecture and design patterns

## Flutter Comprehensive Analysis Framework

### 1. Quick Fixes & Best Practices Analysis
- **Widget Optimization**: Const constructors, unnecessary rebuilds, widget keys
- **Dart Language**: Null safety, async/await patterns, type annotations
- **State Management**: Proper state lifting, disposal, and lifecycle management
- **Code Organization**: File structure, imports, and Flutter project organization
- **UI/UX Patterns**: Material Design, Cupertino, responsive design
- **Testing**: Widget tests, unit tests, and integration test patterns

### 2. Flutter Security Analysis
- **Data Protection**: Secure storage, encryption, and sensitive data handling
- **Authentication**: User authentication, token management, biometric auth
- **Network Security**: HTTPS enforcement, certificate pinning, API security
- **Platform Security**: Android/iOS specific security considerations
- **Input Validation**: Form validation, user input sanitization
- **Dependency Security**: Package vulnerabilities and supply chain security

### 3. Flutter Performance Analysis
- **Widget Performance**: Build method optimization, RepaintBoundary usage
- **Rendering Performance**: Frame rate, jank detection, overdraw prevention
- **Memory Management**: Widget disposal, image caching, memory leaks
- **Network Performance**: API optimization, caching strategies, offline support
- **Storage Performance**: Database queries, file I/O, SharedPreferences
- **Platform Performance**: iOS/Android specific optimizations

### 4. Flutter Architectural Assessment
- **State Management Architecture**: Riverpod, BLoC, Provider implementation
- **Widget Architecture**: Composition, reusability, separation of concerns
- **Data Layer**: Repository pattern, service layer, API integration
- **Navigation Architecture**: Routing patterns, deep linking, navigation state
- **Dependency Injection**: Service locator, dependency management
- **Platform Integration**: Native functionality, platform channels

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of the Flutter codebase across all review dimensions
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., BP-001, SEC-001, PERF-001, ARCH-001)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Widget Performance, Security, Data Protection, Network Security, Memory Management, State Management Architecture, Widget Architecture, Navigation Architecture, Platform Integration, Null Safety, Async Patterns
- **description**: Detailed explanation of the issue or observation
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and potential consequences for the app
- **recommendation**: Specific, actionable fix with Flutter/Dart code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements across all dimensions, ordered by impact and effort.

This code is written in DART for a FLUTTER application. Please provide comprehensive Flutter-specific advice.

{{SCHEMA_INSTRUCTIONS}}`,
  },

  // Python-specific prompts
  python: {
    consolidated: `# Python Consolidated Code Review

You are a senior Python developer performing a comprehensive consolidated review of a Python codebase.

## Your Task

Analyze the provided Python code comprehensively and provide a single consolidated response organized by priority. Focus on Python-specific best practices and patterns:

1. **Code Quality & Bugs**: Identify obvious bugs, logic errors, exception handling issues, and missing error handling
2. **Python Best Practices**: Evaluate adherence to PEP 8, PEP 257, and other Python conventions
3. **Architecture & Structure**: Evaluate code organization, module structure, and package design
4. **Security Concerns**: Look for security vulnerabilities, input validation issues, and unsafe practices
5. **Performance Considerations**: Identify inefficient algorithms, opportunities for optimization using Python idioms
6. **Documentation & Testing**: Assess docstrings, type hints, and test coverage
7. **Dependencies & Environment**: Evaluate dependency management, virtual environments, and packaging

## Python-Specific Focus Areas

### Code Style & Conventions
- PEP 8 compliance (line length, naming conventions, imports)
- PEP 257 docstring conventions
- Proper use of Python idioms and patterns
- Type hints usage (PEP 484, 526, 544)

### Language Features
- Proper use of Python 3.x features (f-strings, pathlib, dataclasses, etc.)
- Context managers and resource management
- Generator expressions and comprehensions
- Decorator usage and design
- Exception handling best practices

### Package & Dependency Management
- requirements.txt, setup.py, or pyproject.toml configuration
- Virtual environment usage
- Import organization and structure
- Package initialization and structure

## Grading Requirements

Provide a comprehensive grade for the codebase using the standard academic scale (A+ to F):

1. **Overall Grade**: Assign a letter grade reflecting the codebase's overall quality
2. **Category Grades**: Grade each category:
   - **Functionality**: How well the code achieves its intended purpose
   - **Code Quality**: Adherence to Python best practices and PEP standards
   - **Documentation**: Quality of docstrings, README files, and type hints
   - **Testing**: Test coverage, quality, and Python testing best practices
   - **Maintainability**: Code organization, modularity, and Pythonic design
   - **Security**: Proper validation, authentication, and security practices
   - **Performance**: Efficiency and proper use of Python performance patterns

3. **Grade Justification**: Provide clear rationale for each grade based on specific evidence

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of the Python codebase quality
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)
- **Overall Grade**: Letter grade (A+ to F) with justification (alongside the 0.0-1.0 score)
- **Category Grades**: Individual letter grades (A+ to F) for each category with explanations:
  - **Functionality** score and grade
  - **Code Quality** (PEP 8/PEP 257 adherence) score and grade
  - **Documentation** (docstrings, type hints) score and grade
  - **Testing** (coverage and Python testing best practices) score and grade
  - **Maintainability** (Pythonic design, modularity) score and grade
  - **Security** score and grade
  - **Performance** (Python idioms and patterns) score and grade

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., CRIT-001, CRIT-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Code Quality, Architecture, Security, Performance, Documentation, Testing, Best Practices, PEP Compliance, Dependency Management
- **description**: Detailed explanation of the issue with Python-specific context
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and potential consequences
- **recommendation**: Specific, actionable Python fix with code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements, ordered by impact and effort. Include positive aspects and strengths worth highlighting.

This code is written in PYTHON. Please provide Python-specific advice and recommendations.

{{CI_DATA}}`,

    architectural: `# Python Architectural Code Review

You are an expert Python architect performing a comprehensive architectural review of a Python codebase.

## Your Task

Analyze the provided Python code from an architectural perspective, focusing on:

1. **Overall Architecture**: Identify architectural patterns and evaluate their appropriateness for Python
2. **Package Structure**: Assess how the code is organized into packages and modules
3. **Dependency Management**: Evaluate how dependencies are managed (requirements.txt, setup.py, pyproject.toml)
4. **Import Strategy**: Check import organization and circular dependency issues
5. **Design Patterns**: Evaluate use of Python-appropriate design patterns
6. **Scalability**: Assess how well the architecture would scale
7. **Maintainability**: Evaluate ease of maintenance and extension

## Python-Specific Architectural Considerations

- Proper use of Python package structure (__init__.py, __main__.py)
- Module organization and namespace design
- Configuration management patterns
- Plugin architecture using entry points
- Async/await patterns for concurrent code
- Database integration patterns (SQLAlchemy, Django ORM, etc.)
- API design patterns (Flask, FastAPI, Django REST)

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of the Python codebase architecture
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., ARCH-001, ARCH-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Package Structure, Dependency Management, Import Strategy, Design Patterns, Scalability, Maintainability, Async Patterns, Database Integration, API Design, Configuration Management
- **description**: Detailed explanation of the issue with Python-specific context
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and potential consequences
- **recommendation**: Specific, actionable fix with Python code examples where appropriate. Where applicable, suggest mature Python packages for common functionality.
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of architectural improvements, ordered by impact and effort.

This code is written in PYTHON. Please provide Python-specific architectural advice.

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,
  },

  // Dart-specific prompts
  dart: {
    'best-practices': `# Dart Best Practices Code Review

You are an **expert Dart engineer** specializing in modern Dart development patterns. Perform a detailed review focused on Dart best practices.

## Dart Best Practices

Evaluate the code against the following Dart-specific best practices:

### 1. Language Features & Syntax
- **Sound Null Safety**: Proper use of nullable and non-nullable types
- **Type System**: Effective use of Dart's type system and type inference
- **Modern Syntax**: Use of Dart 3.x features (records, patterns, sealed classes)
- **Collections**: Efficient use of List, Set, Map, and collection methods
- **String Handling**: Proper string interpolation and manipulation
- **Control Flow**: Effective use of control flow statements and expressions

### 2. Async Programming
- **Future and Stream**: Proper async/await patterns and stream handling
- **Error Handling**: Comprehensive error handling in async operations
- **Concurrency**: Effective use of isolates for CPU-intensive tasks
- **Resource Management**: Proper cleanup of streams and subscriptions
- **Performance**: Avoiding blocking operations on the main isolate

### 3. Object-Oriented Programming
- **Class Design**: Proper class structure and inheritance patterns
- **Mixins**: Effective use of mixins for code reuse
- **Abstract Classes**: Appropriate use of abstract classes and interfaces
- **Constructors**: Proper constructor patterns and factory constructors
- **Encapsulation**: Appropriate use of private members and getters/setters

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall Dart best-practices assessment of the codebase
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., BP-001, BP-002)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Language Features, Null Safety, Async Programming, Object-Oriented Design, Collection Usage, Error Handling, Code Organization, Documentation
- **description**: Detailed explanation of the Dart-specific pattern or practice that could be improved
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters for type safety, maintainability, or performance
- **recommendation**: Specific, actionable guidance with Dart code examples
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements, ordered by impact and effort.

This code is written in DART. Please provide Dart-specific advice.

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,

    comprehensive: `# Dart Comprehensive Code Review

You are an **expert Dart engineer** performing a comprehensive code review that combines multiple analysis perspectives in a single thorough assessment.

## Your Task

Analyze the provided Dart code from **all** of the following perspectives in a single comprehensive review:

1. **Quick Fixes & Best Practices** - Dart language improvements and coding standards
2. **Security Analysis** - Dart-specific security patterns and vulnerabilities
3. **Performance Review** - Dart performance optimization and efficiency
4. **Architectural Assessment** - Dart application architecture and design patterns

## Dart Comprehensive Analysis Framework

### 1. Dart Quick Fixes & Best Practices Analysis
- **Language Features**: Null safety, type system, modern Dart syntax
- **Async Programming**: Future/Stream patterns, async/await usage
- **Object-Oriented Design**: Classes, mixins, abstract classes, interfaces
- **Functional Programming**: Higher-order functions, collections, immutability
- **Code Organization**: Library structure, imports, exports, documentation
- **Error Handling**: Exception patterns, validation, defensive programming

### 2. Dart Security Analysis
- **Input Validation**: Data sanitization and validation patterns
- **Type Safety**: Null safety implementation and type security
- **Data Protection**: Sensitive data handling in Dart applications
- **Package Security**: Dependency vulnerabilities and supply chain security
- **Code Injection**: Prevention of code injection vulnerabilities
- **Configuration Security**: Secure configuration management

### 3. Dart Performance Analysis
- **Memory Management**: Garbage collection, memory leaks, object lifecycle
- **Collection Performance**: Efficient use of List, Set, Map operations
- **String Performance**: String manipulation and interpolation efficiency
- **Async Performance**: Efficient async operations and isolate usage
- **Computation Optimization**: Algorithm efficiency and complexity
- **I/O Performance**: File operations, network requests, database queries

### 4. Dart Architectural Assessment
- **Library Design**: Package structure, API design, modularity
- **Dependency Management**: Coupling, cohesion, dependency injection
- **Design Patterns**: Proper implementation of Dart-appropriate patterns
- **Abstraction Layers**: Interface design and abstraction levels
- **Code Reusability**: DRY principles, mixins, and extension methods
- **Testing Architecture**: Unit testing, mocking, and test organization

This comprehensive Dart review should provide a complete picture of the Dart codebase's health and a clear roadmap for improvement across all critical Dart development dimensions.

## Output Format

Provide your response as structured JSON with the following schema:

### Executive Summary
- Overall assessment of the Dart codebase across all review dimensions
- Key strengths and critical concerns
- Overall score (0.0-1.0) and confidence score (0.0-1.0)

### Findings Array
Each finding must include:
- **id**: Unique identifier (e.g., BP-001, SEC-001, PERF-001, ARCH-001)
- **title**: Brief descriptive title
- **severity**: CRITICAL | HIGH | MEDIUM | LOW | INFO
- **category**: One of: Language Features, Null Safety, Async Programming, Input Validation, Data Protection, Memory Management, Collection Performance, Library Design, Dependency Management, Design Patterns, Testing Architecture
- **description**: Detailed explanation of the issue or observation
- **location**: File path and line number(s) where applicable
- **impact**: Why this matters and potential consequences
- **recommendation**: Specific, actionable fix with Dart code examples where appropriate
- **effort**: LOW | MEDIUM | HIGH (estimated fix effort)
- **confidence**: 0.0-1.0 (how confident you are in this finding)

### Recommendations
Prioritized list of improvements across all dimensions, ordered by impact and effort.

This code is written in DART. Please provide Dart-specific advice.

{{CI_DATA}}

{{SCHEMA_INSTRUCTIONS}}`,
  },

  // Additional frameworks and languages follow the same pattern...
};

/**
 * Get a bundled prompt template with template system integration
 *
 * @param reviewType Type of review
 * @param language Programming language
 * @param framework Framework (optional)
 * @returns The prompt template or undefined if not found
 *
 * This function checks for templates from the template system first,
 * and falls back to bundled prompts if templates are not available.
 */
export function getBundledPrompt(
  reviewType: ReviewType,
  language?: string,
  framework?: string,
): string | undefined {
  // Try using the template system first if available
  if (USE_TEMPLATE_SYSTEM && checkTemplatesAvailability()) {
    const template = getPromptTemplate(reviewType, language, framework);
    if (template) {
      logger.debug(
        `Using template for reviewType=${reviewType}, language=${language}, framework=${framework}`,
      );
      return template;
    }
    // Log a warning if template not found but system is available
    logger.debug(
      `Template not found in template system for reviewType=${reviewType}, language=${language}, framework=${framework}. Falling back to bundled prompt.`,
    );
  }

  // Fallback to hard-coded prompts
  // First try framework-specific prompt if framework is provided
  if (
    language &&
    framework &&
    bundledPrompts[`${language.toLowerCase()}:${framework.toLowerCase()}`] &&
    bundledPrompts[`${language.toLowerCase()}:${framework.toLowerCase()}`][reviewType]
  ) {
    return bundledPrompts[`${language.toLowerCase()}:${framework.toLowerCase()}`][reviewType];
  }

  // Then try language-specific prompt
  if (
    language &&
    bundledPrompts[language.toLowerCase()] &&
    bundledPrompts[language.toLowerCase()][reviewType]
  ) {
    return bundledPrompts[language.toLowerCase()][reviewType];
  }

  // Fallback to generic prompt
  if (bundledPrompts.generic?.[reviewType]) {
    return bundledPrompts.generic[reviewType];
  }

  return undefined;
}
